/**
 * Basic Configuration Step Component
 * Handles application name, description, and owner selection
 */

import React, { useCallback } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';

import type { RootState } from '../../../../services/serviceTypes';
import type { StepFormMessage } from '../../data-layer/types';
import { setSavedStepData } from '../../data-store/copilotSlice';
import useFormValidation from '../../hooks/useFormValidation';
import { FORM_RESPONSE_TEXTS } from '../../services/responseTextService';
import { isEditMode } from '../../utils/modeUtils';
import type { ParsedField } from '../../utils/templateParsers';
import ErrorBanner from '../ErrorBanner/ErrorBanner';
import ExpandableForm from '../ExpandableForm/ExpandableForm';
import ResponseWithForm from '../ResponseWithForm/ResponseWithForm';
import StepRenderer from '../StepRenderer/StepRenderer';

import './StepComponents.css';

interface BasicConfigStepProps {
  message: StepFormMessage;
  onFormSubmit: (
    stepName: string,
    formData: Record<string, unknown>,
    isValid: boolean
  ) => void;
}

/**
 * BasicConfigStep
 */
function BasicConfigStep({ message, onFormSubmit }: BasicConfigStepProps) {
  const dispatch = useDispatch();
  const [showError, setShowError] = React.useState(false);

  const store = useStore() as { getState: () => unknown };
  // Get persisted form data from Redux for validation
  const persistedFormData = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    (state: RootState) => state.copilot.stepFormData.General ?? {}
  );

  // Get endpointKey to determine if application name should be disabled
  const endpointKey = useSelector(
    (state: RootState) =>
      state.copilot.endpointKey || state.copilot.ui.endpointKey
  );

  const isEditModeViaParam = useSelector(
    (state: RootState) => state.copilot.ui.endpointKey
  );

  // Check if step is completed
  const isCompletedCheck = useSelector(
    (state: RootState) => state.copilot.navigation.completedSteps ?? {}
  );

  const btnlabel = isCompletedCheck.includes('General');

  const { validateForm, localFields, resetValidationState } =
    useFormValidation();

  // Create modified fields with disabled state for application name
  const modifiedFields = React.useMemo(
    () =>
      localFields.map((field) => {
        // Disable application name field when endpointKey exists
        if (
          field.attributeName === 'securitysystems.systemname' &&
          endpointKey
        ) {
          return {
            ...field,
            fieldDisabled: true,
          };
        }

        return field;
      }),
    [localFields, endpointKey]
  );

  // Extract owner type for useEffect dependency
  const ownerType = persistedFormData['endpoints.ownerType'] as
    | string[]
    | undefined;

  // Reset validation when owner type changes
  React.useEffect(() => {
    resetValidationState();
    setShowError(false); // Reset error state when owner type changes
  }, [ownerType, resetValidationState]);

  const validateAndContinue = useCallback(async () => {
    setShowError(false);

    // Check owner type selection to determine if UserGroup field is required
    const isUserGroupSelected =
      Array.isArray(ownerType) &&
      (ownerType.includes('UserGroup') || ownerType.includes('User Group'));

    // Define required fields based on owner type selection
    const requiredFields: ParsedField[] = [
      {
        type: 'TextField',
        label: 'Application Name',
        attributeName: 'securitysystems.systemname',
        attributeKey: '1',
        required: true,
        defaultValue: '',
        placeholder: 'Enter application name',
      },
    ];

    // Convert persistedFormData to the format expected by validateForm
    const formData = Object.entries(persistedFormData).reduce<
      Record<string, { value: string; hasError: boolean }>
    >((acc, [key, value]) => {
      acc[key] = { value: String(value || ''), hasError: false };

      return acc;
    }, {});

    // Add UserGroup field as required if UserGroup is selected
    if (isUserGroupSelected) {
      requiredFields.push({
        type: 'Dropdown',
        label: 'User Group',
        attributeName: 'endpoints.ownerkey',
        attributeKey: '2',
        required: true,
        defaultValue: '',
        placeholder: 'Select user group',
      });

      // Clear user selection for UserGroup validation
      const ownerKeyValue = formData['endpoints.ownerkey'].value || '';

      try {
        const parsed = JSON.parse(ownerKeyValue) as unknown;

        if (
          parsed &&
          typeof parsed === 'object' &&
          'id' in parsed &&
          'value' in parsed
        ) {
          // This looks like a user selection, clear it for UserGroup validation
          formData['endpoints.ownerkey'].value = '';
        }
      } catch {
        // Not JSON, treat as string - if it's not empty, it might be a group
      }
    }

    let isValid = false;

    if (!endpointKey && !btnlabel) {
      isValid = await validateForm(requiredFields, formData);
    } else {
      isValid = true;
    }

    // Perform both client-side and API validation

    if (isValid || endpointKey || btnlabel) {
      // Save to savedStepData on successful validation
      dispatch(
        setSavedStepData({
          stepName: message.stepName ?? 'General',
          formData: persistedFormData,
        })
      );

      onFormSubmit(message.stepName ?? 'General', persistedFormData, isValid);
    } else {
      // Show error banner when validation fails
      setShowError(true);
    }

    return !!(isValid && !isEditMode(store.getState));
  }, [
    ownerType,
    persistedFormData,
    endpointKey,
    btnlabel,
    store.getState,
    validateForm,
    dispatch,
    message.stepName,
    onFormSubmit,
  ]);

  const formContent = (
    <>
      <ExpandableForm
        stepName={message.stepName ?? 'General'}
        heading={message.heading}
        buttonLabel={btnlabel ? 'Update' : message.buttonLabel}
        buttonkind='outlined'
        onSave={validateAndContinue}>
        <StepRenderer
          stepName='General'
          validationFields={modifiedFields}
          className='Copilot-StepContent'
        />
      </ExpandableForm>
      {showError && (
        <ErrorBanner message='An error occurred while saving. Please try later..' />
      )}
    </>
  );

  return !isEditModeViaParam ? (
    <ResponseWithForm responseText={FORM_RESPONSE_TEXTS.GENERAL}>
      {formContent}
    </ResponseWithForm>
  ) : (
    formContent
  );
}

export default BasicConfigStep;
